<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("agent_commission.rounds_title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">

            <!-- Create / Preview panel -->
            <card class="mb-4">
                <template slot="header">
                    <h3 class="mb-0">{{ $t("agent_commission.round.create") }}</h3>
                </template>

                <div class="row align-items-end">
                    <div class="col-md-3">
                        <base-input :label="$t('agent_commission.round.select_month')">
                            <el-date-picker
                                v-model="selectedMonth"
                                type="month"
                                format="yyyy-MM"
                                value-format="yyyy-MM"
                                :placeholder="$t('agent_commission.round.select_month')"
                                class="w-100"
                            ></el-date-picker>
                        </base-input>
                    </div>
                    <div class="col-md-3">
                        <base-input :label="$t('agent_commission.round.note')">
                            <el-input v-model="createNote" :placeholder="$t('agent_commission.round.note')"></el-input>
                        </base-input>
                    </div>
                    <div class="col-md-6 pb-3">
                        <!-- spec 2026-10-07-bo-form-draft-clear-design.md §4 A11: the
                             clear button belongs to this card (leftmost of its buttons),
                             not to the page header -->
                        <form-clear-button :disabled="creating || previewing" @click="clearForm" />
                        <base-button type="info" :loading="previewing" @click="doPreview">
                            {{ $t("agent_commission.round.preview") }}
                        </base-button>
                        <base-button type="primary" :loading="creating" @click="doCreate">
                            {{ $t("agent_commission.round.create") }}
                        </base-button>
                    </div>
                </div>

                <!-- Preview result -->
                <div v-if="previewResult" class="mt-3">
                    <h4>{{ $t("agent_commission.round.preview_title") }}: {{ previewResult.code }}</h4>
                    <p>
                        {{ $t("agent_commission.round.period_start") }}: {{ formatDate(previewResult.period_start) }}
                        &nbsp;—&nbsp;
                        {{ $t("agent_commission.round.period_end") }}: {{ formatDate(previewResult.period_end) }}
                    </p>
                    <p>
                        {{ $t("agent_commission.round.total_sales") }}: {{ formatAmount(previewResult.total_sales) }}
                        &nbsp;|&nbsp;
                        {{ $t("agent_commission.round.total_commission") }}: {{ formatAmount(previewResult.total_commission) }}
                    </p>
                    <el-table :data="previewResult.agents || []" header-row-class-name="thead-light" size="small">
                        <el-table-column :label="$t('agent_commission.agent.agent_name')" prop="agent_name"></el-table-column>
                        <el-table-column :label="$t('agent_commission.round.total_sales')">
                            <div slot-scope="{ row }">{{ formatAmount(row.total_sales) }}</div>
                        </el-table-column>
                        <el-table-column :label="$t('agent_commission.agent.tier_percent')">
                            <div slot-scope="{ row }">{{ row.tier_percent }}%</div>
                        </el-table-column>
                        <el-table-column :label="$t('agent_commission.agent.commission_amount')">
                            <div slot-scope="{ row }">{{ formatAmount(row.commission_amount) }}</div>
                        </el-table-column>
                        <el-table-column :label="$t('agent_commission.agent.order_count')" prop="order_count"></el-table-column>
                    </el-table>
                </div>
            </card>

            <!-- Rounds list -->
            <card>
                <template slot="header">
                    <h3 class="mb-0">{{ $t("agent_commission.rounds_title") }}</h3>
                </template>

                <div v-if="rounds.length === 0" class="text-center text-muted py-4">
                    {{ $t("agent_commission.empty_rounds") }}
                </div>

                <el-table v-else :data="rounds" header-row-class-name="thead-light">
                    <el-table-column :label="$t('agent_commission.round.code')" prop="code"></el-table-column>
                    <el-table-column :label="$t('agent_commission.round.period')">
                        <div slot-scope="{ row }">
                            {{ formatDate(row.period_start) }} — {{ formatDate(row.period_end) }}
                        </div>
                    </el-table-column>
                    <el-table-column :label="$t('agent_commission.round.status')">
                        <div slot-scope="{ row }">
                            <span class="badge badge-success">{{ $t('agent_commission.round.status_closed') }}</span>
                        </div>
                    </el-table-column>
                    <el-table-column :label="$t('agent_commission.round.total_sales')">
                        <div slot-scope="{ row }">{{ formatAmount(row.total_sales) }}</div>
                    </el-table-column>
                    <el-table-column :label="$t('agent_commission.round.total_commission')">
                        <div slot-scope="{ row }">{{ formatAmount(row.total_commission) }}</div>
                    </el-table-column>
                    <el-table-column :label="$t('agent_commission.round.agent_count')" prop="agent_count"></el-table-column>
                    <el-table-column :label="$t('agent_commission.round.order_count')" prop="order_count"></el-table-column>
                    <el-table-column :label="$t('agent_commission.round.created_at')">
                        <div slot-scope="{ row }">{{ formatDate(row.created_at) }}</div>
                    </el-table-column>
                    <el-table-column :label="$t('actions')" align="right" min-width="150">
                        <div slot-scope="{ row }" class="d-flex justify-content-end">
                            <base-button type="info" size="sm" @click="viewDetail(row)">
                                <i class="fa fa-eye"></i>
                            </base-button>
                            <base-button type="danger" size="sm" @click="confirmDelete(row)">
                                {{ $t("agent_commission.round.delete") }}
                            </base-button>
                        </div>
                    </el-table-column>
                </el-table>
            </card>
        </div>
    </div>
</template>

<script>
import { Table, TableColumn, DatePicker, Input } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import formDraft from "@/mixins/formDraft";
import FormClearButton from "@/components/FormClearButton.vue";

function currentMonthCode() {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    return `${now.getFullYear()}-${mm}`;
}

export default {
    name: "AgentCommissionRounds",
    mixins: [formDraft],
    components: {
        FormClearButton,
        RouteBreadCrumb,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [DatePicker.name]: DatePicker,
        [Input.name]: Input,
    },
    data() {
        return {
            selectedMonth: "",
            createNote: "",
            previewing: false,
            creating: false,
        };
    },
    computed: {
        ...mapGetters({
            rounds: "AgentCommission/getRounds",
            previewResult: "AgentCommission/getPreviewResult",
        }),
    },
    mounted() {
        this.loadRounds();
        // Default to current month code
        this.selectedMonth = currentMonthCode();
    },
    methods: {
        ...mapActions({
            fetchRounds: "AgentCommission/fetchRounds",
            previewRound: "AgentCommission/previewRound",
            createRound: "AgentCommission/createRound",
            deleteRound: "AgentCommission/deleteRound",
        }),
        // formDraft contract (spec 2026-10-07-bo-form-draft-clear-design.md
        // §3 F1, §4 A11) — the "create round" card only: month back to the
        // current month (the mounted() default), note emptied, preview result
        // dropped, busy flags back to false (the data() defaults; the clear
        // button is disabled while either request runs, so only
        // afterSaveReset() can hit a live flag, and doCreate's finally sets it
        // false again anyway). The rounds list below is not form input and stays.
        resetForm() {
            this.selectedMonth = currentMonthCode();
            this.createNote = "";
            this.creating = false;
            this.previewing = false;
            this.$store.commit("AgentCommission/CLEAR_PREVIEW");
        },
        async loadRounds() {
            try {
                await this.fetchRounds();
            } catch (err) {
                // handled in module
            }
        },
        async doPreview() {
            if (!this.selectedMonth) {
                swal.fire({
                    title: this.$t("agent_commission.validation.code_required"),
                    icon: "warning",
                    confirmButtonClass: "btn btn-warning btn-fill",
                    buttonsStyling: false,
                });
                return;
            }
            this.previewing = true;
            try {
                await this.previewRound(this.selectedMonth);
            } catch (err) {
                const msg = err.response && err.response.data && err.response.data.Cause
                    ? err.response.data.Cause
                    : err.message;
                swal.fire({ title: msg, icon: "error", confirmButtonClass: "btn btn-danger btn-fill", buttonsStyling: false });
            } finally {
                this.previewing = false;
            }
        },
        async doCreate() {
            if (!this.selectedMonth) {
                swal.fire({
                    title: this.$t("agent_commission.validation.code_required"),
                    icon: "warning",
                    confirmButtonClass: "btn btn-warning btn-fill",
                    buttonsStyling: false,
                });
                return;
            }
            this.creating = true;
            try {
                const payload = { code: this.selectedMonth };
                if (this.createNote) payload.note = this.createNote;
                await this.createRound(payload);
                swal.fire({
                    title: this.$t("agent_commission.create_success"),
                    icon: "success",
                    confirmButtonClass: "btn btn-success btn-fill",
                    buttonsStyling: false,
                });
                // Saved: clear the whole card (was: only the note). A failed
                // create goes to the catch and keeps what was typed.
                this.afterSaveReset();
                await this.loadRounds();
            } catch (err) {
                const msg = err.response && err.response.data && err.response.data.Cause
                    ? err.response.data.Cause
                    : err.message;
                swal.fire({ title: msg, icon: "error", confirmButtonClass: "btn btn-danger btn-fill", buttonsStyling: false });
            } finally {
                this.creating = false;
            }
        },
        viewDetail(row) {
            this.$router.push(`/backoffice/agent-commission/rounds/${row.id}`);
        },
        confirmDelete(row) {
            swal.fire({
                title: this.$t("agent_commission.round.confirm_delete"),
                text: this.$t("agent_commission.round.confirm_delete_msg"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonClass: "btn btn-danger btn-fill",
                cancelButtonClass: "btn btn-secondary btn-fill",
                confirmButtonText: this.$t("confirm"),
                cancelButtonText: this.$t("cancel"),
                buttonsStyling: false,
            }).then(async result => {
                if (result.value) {
                    try {
                        await this.deleteRound(row.id);
                        swal.fire({
                            title: this.$t("agent_commission.delete_success"),
                            icon: "success",
                            confirmButtonClass: "btn btn-success btn-fill",
                            buttonsStyling: false,
                        });
                        await this.loadRounds();
                    } catch (err) {
                        const msg = err.response && err.response.data && err.response.data.Cause
                            ? err.response.data.Cause
                            : err.message;
                        swal.fire({ title: msg, icon: "error", confirmButtonClass: "btn btn-danger btn-fill", buttonsStyling: false });
                    }
                }
            });
        },
        formatDate(val) {
            if (!val) return "-";
            return val.replace("T", " ").substring(0, 16);
        },
        formatAmount(val) {
            if (val == null) return "-";
            return Number(val).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },
};
</script>
